import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const TimesheetGrid = ({ timeEntries, projects, selectedDate, onDateChange, onUpdateEntry, onDeleteEntry, onAddEntry }) => {
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({
    projectId: '',
    task: '',
    startTime: '',
    endTime: '',
    date: selectedDate
  });

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diff = (end - start) / (1000 * 60 * 60); // Convert to hours
    return Math.max(0, diff);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry({ ...entry });
  };

  const handleSaveEdit = () => {
    if (editingEntry) {
      const duration = calculateDuration(editingEntry?.startTime, editingEntry?.endTime);
      onUpdateEntry({
        ...editingEntry,
        duration
      });
      setEditingEntry(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
  };

  const handleAddNewEntry = () => {
    if (newEntry?.projectId && newEntry?.task && newEntry?.startTime && newEntry?.endTime) {
      const duration = calculateDuration(newEntry?.startTime, newEntry?.endTime);
      onAddEntry({
        ...newEntry,
        duration,
        id: Date.now()?.toString()
      });
      setNewEntry({
        projectId: '',
        task: '',
        startTime: '',
        endTime: '',
        date: selectedDate
      });
      setShowAddForm(false);
    }
  };

  const projectOptions = projects?.map(project => ({
    value: project?.id,
    label: `${project?.name} - ${project?.client}`
  }));

  const filteredEntries = timeEntries?.filter(entry => entry?.date === selectedDate);
  const totalHours = filteredEntries?.reduce((sum, entry) => sum + entry?.duration, 0);
  const billableHours = filteredEntries?.filter(entry => entry?.billable)?.reduce((sum, entry) => sum + entry?.duration, 0);

  return (
    <div className="flex-1 bg-surface">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-foreground">Timesheet</h1>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const prevDate = new Date(selectedDate);
                  prevDate?.setDate(prevDate?.getDate() - 1);
                  onDateChange(prevDate?.toISOString()?.split('T')?.[0]);
                }}
              >
                <Icon name="ChevronLeft" size={20} />
              </Button>
              <div className="text-lg font-medium text-foreground min-w-64 text-center">
                {formatDate(selectedDate)}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const nextDate = new Date(selectedDate);
                  nextDate?.setDate(nextDate?.getDate() + 1);
                  onDateChange(nextDate?.toISOString()?.split('T')?.[0]);
                }}
              >
                <Icon name="ChevronRight" size={20} />
              </Button>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowAddForm(true)}
            >
              Add Entry
            </Button>
            <Button
              variant="ghost"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => onDateChange(new Date()?.toISOString()?.split('T')?.[0])}
            >
              Today
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-muted-foreground">Total:</span>
            <span className="font-medium text-foreground">{totalHours?.toFixed(2)}h</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="DollarSign" size={16} className="text-success" />
            <span className="text-muted-foreground">Billable:</span>
            <span className="font-medium text-success">{billableHours?.toFixed(2)}h</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={16} className="text-muted-foreground" />
            <span className="text-muted-foreground">Goal:</span>
            <span className="font-medium text-foreground">8.0h</span>
          </div>
        </div>
      </div>
      {/* Add Entry Form */}
      {showAddForm && (
        <div className="p-6 border-b border-border bg-muted/20">
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-3">
              <Select
                label="Project"
                placeholder="Select project"
                options={projectOptions}
                value={newEntry?.projectId}
                onChange={(value) => setNewEntry({ ...newEntry, projectId: value })}
              />
            </div>
            <div className="col-span-3">
              <Input
                label="Task Description"
                type="text"
                placeholder="What did you work on?"
                value={newEntry?.task}
                onChange={(e) => setNewEntry({ ...newEntry, task: e?.target?.value })}
              />
            </div>
            <div className="col-span-2">
              <Input
                label="Start Time"
                type="time"
                value={newEntry?.startTime}
                onChange={(e) => setNewEntry({ ...newEntry, startTime: e?.target?.value })}
              />
            </div>
            <div className="col-span-2">
              <Input
                label="End Time"
                type="time"
                value={newEntry?.endTime}
                onChange={(e) => setNewEntry({ ...newEntry, endTime: e?.target?.value })}
              />
            </div>
            <div className="col-span-2 flex space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAddNewEntry}
                disabled={!newEntry?.projectId || !newEntry?.task || !newEntry?.startTime || !newEntry?.endTime}
              >
                Add
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Timesheet Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-muted/30 sticky top-0">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Project</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Task</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Start Time</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">End Time</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Duration</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Billable</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries?.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="mb-2">
                      <img 
                        src="/assets/images/image-1761885647241.png" 
                        alt="Time tracking illustration showing organized schedule and productivity management"
                        className="w-24 h-24 object-contain opacity-50"
                      />
                    </div>
                    <div className="text-muted-foreground">No time entries for this date</div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => setShowAddForm(true)}
                    >
                      Add First Entry
                    </Button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredEntries?.map((entry) => (
                <tr key={entry?.id} className="border-b border-border hover:bg-muted/30">
                  {editingEntry?.id === entry?.id ? (
                    <>
                      <td className="p-4">
                        <Select
                          options={projectOptions}
                          value={editingEntry?.projectId}
                          onChange={(value) => setEditingEntry({ ...editingEntry, projectId: value })}
                        />
                      </td>
                      <td className="p-4">
                        <input
                          type="text"
                          value={editingEntry?.task}
                          onChange={(e) => setEditingEntry({ ...editingEntry, task: e?.target?.value })}
                          className="w-full px-2 py-1 border border-border rounded text-sm"
                        />
                      </td>
                      <td className="p-4">
                        <input
                          type="time"
                          value={editingEntry?.startTime}
                          onChange={(e) => setEditingEntry({ ...editingEntry, startTime: e?.target?.value })}
                          className="w-full px-2 py-1 border border-border rounded text-sm"
                        />
                      </td>
                      <td className="p-4">
                        <input
                          type="time"
                          value={editingEntry?.endTime}
                          onChange={(e) => setEditingEntry({ ...editingEntry, endTime: e?.target?.value })}
                          className="w-full px-2 py-1 border border-border rounded text-sm"
                        />
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {calculateDuration(editingEntry?.startTime, editingEntry?.endTime)?.toFixed(2)}h
                      </td>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={editingEntry?.billable}
                          onChange={(e) => setEditingEntry({ ...editingEntry, billable: e?.target?.checked })}
                          className="rounded border-border"
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm" onClick={handleSaveEdit}>
                            <Icon name="Check" size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                            <Icon name="X" size={16} />
                          </Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-4">
                        <div>
                          <div className="font-medium text-sm text-foreground">
                            {projects?.find(p => p?.id === entry?.projectId)?.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {projects?.find(p => p?.id === entry?.projectId)?.client}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-foreground">{entry?.task}</td>
                      <td className="p-4 text-sm text-muted-foreground">{entry?.startTime}</td>
                      <td className="p-4 text-sm text-muted-foreground">{entry?.endTime}</td>
                      <td className="p-4 text-sm font-medium text-foreground">{entry?.duration?.toFixed(2)}h</td>
                      <td className="p-4">
                        {entry?.billable ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                            <Icon name="Check" size={12} className="mr-1" />
                            Billable
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                            Non-billable
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditEntry(entry)}
                          >
                            <Icon name="Edit2" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteEntry(entry?.id)}
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimesheetGrid;